import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Flame, Trash2, Sparkles, Bell, Heart, Undo2 } from "@/lib/icons";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { HabitLadder } from "@/components/HabitLadder";
import { ModalClose } from "@/components/ui/ModalClose";
import { HabitPause } from "@/components/HabitPause";
import { FocusBadge, FocusPicker } from "@/components/HabitFocus";
import { RubricEditor, TierInput, TierBadge } from "@/components/rubric/RubricParts";
import { MeasurePanel, RubricProposalCard } from "@/components/client/HabitMeasure";
import { StagePanel } from "@/components/client/StagePanel";
import { StageBuilder } from "@/components/client/StageBuilder";
import { DEFAULT_RUBRIC, needsValue, isNumeric } from "@/lib/rubric";
import { AppearancePicker } from "@/components/client/AppearancePicker";
import { HABIT_COLORS } from "@/lib/habitColors";

export const FINAL_GOALS = [
  { v: 1, label: "1×/week" },
  { v: 2, label: "2×/week" },
  { v: 3, label: "3×/week" },
  { v: 5, label: "5×/week" },
  { v: 7, label: "Every day" },
];

const HABIT_KINDS = [{ v: "start", label: "Start good habit" }, { v: "stop", label: "Stop bad habit" }];
const HABIT_CATS = [
  { v: "sleep", label: "🌙 Sleep" }, { v: "exercise", label: "💪 Exercise" }, { v: "productivity", label: "⚡ Productivity" },
  { v: "nutrition", label: "🥗 Nutrition" }, { v: "mindfulness", label: "🧘 Mindfulness" }, { v: "social", label: "🤝 Social" },
  { v: "learning", label: "📚 Learning" }, { v: "other", label: "✨ Other" },
];

function Chips({ options, value, onPick, testPrefix, onCls, size = "text-xs px-3 py-1.5" }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button type="button" key={o.v} data-testid={`${testPrefix}-${o.v}`} onClick={() => onPick(o.v)}
          className={`${size} rounded-full font-semibold transition ${value === o.v ? onCls : "bg-stone-100 text-stone-700"}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function HabitForm({ value, onChange, onSubmit }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const [suggesting, setSuggesting] = useState(false);
  const [why, setWhy] = useState("");
  const suggest = async () => {
    if (!value.title.trim()) { toast("Give the habit a name first, then I can suggest how to measure it."); return; }
    setSuggesting(true);
    try {
      const r = await api.post("/habits/rubric/suggest", { title: value.title, kind: value.kind, category: value.category });
      set({ rubric: r.data.rubric, rubric_source: r.data.source });
      setWhy(r.data.rubric.why || "");
    } catch (e) {
      toast("Couldn't get a suggestion right now — you can still set it by hand.");
    } finally { setSuggesting(false); }
  };
  return (
    <motion.form initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
      onSubmit={onSubmit} className="warm-card p-6 space-y-3">
      <Chips options={HABIT_KINDS} value={value.kind} onPick={(kind) => set({ kind })} testPrefix="habit-kind" onCls="bg-stone-900 text-white" size="text-sm px-4 py-2" />
      <input data-testid="habit-title" value={value.title} onChange={(e) => set({ title: e.target.value })}
        placeholder={value.kind === "start" ? "e.g. Morning walk for 15 min" : "e.g. No screens after 10pm"}
        className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none" />
      <textarea data-testid="habit-description" value={value.description} onChange={(e) => set({ description: e.target.value })}
        placeholder="Why does this matter to you?" rows={2}
        className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none" />
      {value.kind === "stop" && (
        <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-3" data-testid="habit-taper-box">
          <div className="text-xs font-bold uppercase tracking-widest text-stone-600 mb-2">How to ease off</div>
          <Chips options={[{ v: "once", label: "Stop at once" }, { v: "taper", label: "Taper down" }]} value={value.taper_start ? "taper" : "once"}
            onPick={(m) => set({ taper_start: m === "taper" ? (value.taper_start || 3) : null })} testPrefix="habit-taper" onCls="bg-sky-700 text-white" />
          {value.taper_start ? (
            <label className="mt-2 flex items-center gap-2 text-xs text-stone-700">Start with an allowance of
              <input type="number" min={1} max={50} data-testid="habit-taper-start" value={value.taper_start} onChange={(e) => set({ taper_start: Math.max(1, Number(e.target.value) || 1) })}
                className="w-16 px-2 py-1 rounded-lg border border-stone-300 text-sm" /> a day. Each week you stay under, it steps down by one — every step earns points.
            </label>
          ) : <p className="text-xs text-stone-500 mt-2">All-or-nothing can be hard. Tapering gives you a daily allowance that shrinks week by week, so smaller days still count as wins.</p>}
        </div>
      )}
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-stone-600 mb-2">Category</div>
        <Chips options={HABIT_CATS} value={value.category} onPick={(category) => set({ category })} testPrefix="habit-cat" onCls="bg-amber-600 text-white" />
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-stone-600 mb-1">Final goal</div>
        <p className="text-xs text-stone-500 mb-2">You'll start at once a week and climb toward this. Beat a goal or hold it 3 weeks to move up.</p>
        <Chips options={FINAL_GOALS} value={value.final_goal} onPick={(final_goal) => set({ final_goal })} testPrefix="habit-goal" onCls="bg-emerald-700 text-white" />
      </div>
      <div className="rounded-2xl bg-stone-50 border border-stone-200 p-4" data-testid="measure-choice">
        <div className="text-xs font-bold uppercase tracking-widest text-stone-600 mb-2">How will you measure it?</div>
        <div className="grid sm:grid-cols-2 gap-2 mb-3">
          {[["single", "Single measure", "One way to measure it, with partial credit if you like."], ["stages", "In stages", "A ladder of stages, each measured its own way. You decide when to move."]].map(([k, t, d]) => (
            <button type="button" key={k} onClick={() => set({ measure_mode: k })} data-testid={`measure-mode-${k}`}
              className={`text-left rounded-xl border p-3 transition ${(value.measure_mode || "single") === k ? "border-amber-500 bg-white ring-2 ring-amber-200" : "border-stone-200 bg-white/60 hover:border-amber-300"}`}>
              <div className="text-sm font-semibold text-stone-900">{t}</div><div className="text-xs text-stone-500 mt-0.5">{d}</div>
            </button>
          ))}
        </div>
        {(value.measure_mode || "single") === "single" ? (<>
          <RubricEditor value={value.rubric || DEFAULT_RUBRIC} onChange={(rubric) => { set({ rubric, rubric_source: "client" }); setWhy(""); }}
            onSuggest={suggest} suggesting={suggesting} why={why} idPrefix="new-rubric" />
          <p className="text-[11px] text-stone-500 mt-2">Getting partway still counts — and you can change this anytime{value.rubric_source === "ai" ? "; your coach sees AI suggestions too" : ""}.</p>
        </>) : (
          <StageBuilder key={value.kind} initial={value.stages} kind={value.kind} inline onChangeStages={(stages) => set({ stages })} title={value.title} category={value.category} />
        )}
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-stone-600 mb-2">Colour & icon</div>
        <AppearancePicker color={value.color} icon={value.icon} onChange={(patch) => set(patch)} idPrefix="new-appearance" />
      </div>
      <button data-testid="habit-save" type="submit" className="amber-btn">Add it</button>
    </motion.form>
  );
}

function CheckInControl({ h, onCheckIn, idPrefix }) {
  const [value, setValue] = useState("");
  const measured = needsValue(h.rubric);
  const ready = !measured || value !== "";
  const tierButtons = !h.completed_today && (h.rubric.type === "self" || (h.stage && h.rubric.tiers.length > 1));
  if (tierButtons) {
    return (
      <div className="flex flex-col gap-2 items-end" data-testid={`${idPrefix}-tier-buttons`}>
        {h.stage && <span className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Stage {h.stage.index + 1} · {h.stage.current}</span>}
        <div className="flex flex-wrap gap-1.5 justify-end">
          {h.rubric.tiers.map((t, i) => (
            <button key={t.key} data-testid={`${idPrefix}-tier-${t.key}`} onClick={() => onCheckIn(h, undefined, t.key)} title={t.rule?.text || ""}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition ${i === 0 ? "bg-stone-900 text-white hover:bg-emerald-700" : "bg-stone-100 text-stone-800 hover:bg-stone-200"}`}>{t.label}</button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2 items-end">
      {measured && !h.completed_today && <TierInput rubric={h.rubric} value={value} onChange={setValue} idPrefix={idPrefix} />}
      <button data-testid={`checkin-btn-${h.id}`} onClick={() => onCheckIn(h, measured ? (isNumeric(h.rubric.type) ? Number(value) : value) : undefined)} disabled={h.completed_today || !ready}
        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition ${h.completed_today ? "bg-emerald-100 text-emerald-800" : "bg-stone-900 text-white hover:bg-amber-700 disabled:opacity-40"}`}>
        <Check className="w-4 h-4" /> {h.completed_today ? "Done for today" : measured ? "Log it" : "Mark done"}
      </button>
      {h.completed_today && h.today_tier && h.rubric.type !== "yesno" && (
        <TierBadge tier={h.today_tier} label={`${h.rubric.tiers.find((t) => t.key === h.today_tier)?.label || h.today_tier}${h.today_value != null ? ` · ${h.today_value}${isNumeric(h.rubric.type) && h.rubric.unit ? ` ${h.rubric.unit}` : ""}` : ""}`} testid={`today-tier-${h.id}`} />
      )}
    </div>
  );
}

function AppearanceRow({ h, onChange }) {
  const [open, setOpen] = useState(false);
  const save = async (patch) => {
    await api.patch(`/habits/${h.id}/appearance`, patch);
    onChange();
  };
  return (
    <div className="mt-3">
      <button onClick={() => setOpen((v) => !v)} data-testid={`appearance-toggle-${h.id}`} className="inline-flex items-center gap-2 text-[11px] font-semibold text-amber-800 hover:text-amber-900">
        <span className={`w-3 h-3 rounded-full ${(HABIT_COLORS[h.color] || HABIT_COLORS.amber).dot}`} /> Colour & icon
      </button>
      {open && <div className="mt-2"><AppearancePicker color={h.color} icon={h.icon} onChange={save} idPrefix={`appearance-${h.id}`} /></div>}
    </div>
  );
}

export function HabitCard({ h, generating, onCheckIn, onUndo, onLevelUp, onReward, onRemind, onRemove, onChange, onFocus, onProposal, embedded }) {
  return (
    <motion.div layout className={`${embedded ? "rounded-2xl bg-stone-50 border border-stone-200 p-4" : `warm-card p-5 sm:p-6 ${h.focus === "keystone" ? "ring-2 ring-amber-300" : ""}`}`} data-testid={`habit-card-${h.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full ${h.kind === "start" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
              {h.kind === "start" ? "Starting" : "Easing off"}
            </span>
            <FocusBadge focus={h.focus} setBy={h.focus_set_by} habitId={h.id} />
            {h.streak > 0 && <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700"><Flame className="w-3.5 h-3.5" /> {h.streak}-day momentum</span>}
          </div>
          {!embedded && <h3 className="font-display text-lg font-bold text-stone-900 mt-2">{h.title}{h.rooted && <span title="Rooted — this habit climbed its whole ladder" data-testid={`habit-rooted-${h.id}`} className="ml-2 inline-block align-middle w-3 h-5 bg-rose-700 [clip-path:polygon(0_0,100%_0,100%_100%,50%_75%,0_100%)]" />}</h3>}
          {h.description && <p className="text-sm text-stone-500 mt-1">{h.description}</p>}
          {h.taper && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-sky-800 bg-sky-50 border border-sky-200 rounded-full px-2.5 py-1" data-testid={`habit-taper-${h.id}`}>
              Taper · allowance {h.taper.allowed} a day{h.taper.allowed < h.taper.start ? ` (started at ${h.taper.start}, ${h.taper.start - h.taper.allowed} step${h.taper.start - h.taper.allowed === 1 ? "" : "s"} down)` : ""}
            </div>
          )}
          <div className="text-xs text-stone-500 mt-2 font-mono">
            {h.total_completions} total · final goal: {FINAL_GOALS.find((g) => g.v === h.final_goal)?.label.toLowerCase()}
          </div>
          <HabitLadder habit={h} onLevelUp={onLevelUp} />
          <MeasurePanel habit={h} onSaved={onChange} />
          <RubricProposalCard habit={h} onAnswer={onProposal} />
          <StagePanel habit={h} onChange={onChange} />
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Focus</span>
            <FocusPicker value={h.focus} habitId={h.id} compact onChange={(f) => onFocus(h, f)} />
          </div>
          <HabitPause habit={h} onChange={onChange} />
          <AppearanceRow h={h} onChange={onChange} />
        </div>
        <div className="flex flex-col gap-2 items-end">
          {!embedded && <CheckInControl h={h} onCheckIn={onCheckIn} idPrefix={`habit-${h.id}`} />}
          {h.completed_today && !embedded && (
            <button data-testid={`undo-checkin-${h.id}`} onClick={() => onUndo(h)} className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-900 font-semibold">
              <Undo2 className="w-3.5 h-3.5" /> Undo — marked by mistake
            </button>
          )}
          <button data-testid={`generate-reward-${h.id}`} onClick={() => onReward(h)} disabled={generating}
            className="inline-flex items-center gap-1.5 text-xs text-amber-800 hover:text-amber-900 font-semibold">
            <Sparkles className="w-3.5 h-3.5" /> {generating ? "Crafting…" : "Get a reward"}
          </button>
          <button data-testid={`remind-habit-${h.id}`} onClick={() => onRemind(h.id)} className="inline-flex items-center gap-1.5 text-xs text-stone-600 hover:text-stone-900 font-semibold">
            <Bell className="w-3.5 h-3.5" /> Remind me
          </button>
          <button data-testid={`remove-habit-${h.id}`} onClick={() => onRemove(h.id)} className="text-xs text-stone-400 hover:text-stone-700 inline-flex items-center gap-1">
            <Trash2 className="w-3 h-3" /> archive
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export function ThankYouModal({ text, onClose }) {
  if (!text) return null;
  return (
    <div className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="thank-you-modal">
      <div className="warm-card relative w-full max-w-md p-8 text-center" onClick={(e) => e.stopPropagation()}>
        <ModalClose onClose={onClose} testid="thank-you-x" />
        <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-amber-400 to-orange-600 flex items-center justify-center shadow-lg">
          <Heart className="w-8 h-8 text-white fill-white" />
        </div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800 mt-4">Beta Pioneer · +40</div>
        <h2 className="font-display text-2xl font-extrabold text-stone-900 mt-1">A whole week. Thank you.</h2>
        <p className="text-sm text-stone-700 mt-3 leading-relaxed" data-testid="thank-you-text">{text}</p>
        <button data-testid="thank-you-close" onClick={onClose} className="amber-btn mt-6 text-sm">Keep going</button>
      </div>
    </div>
  );
}
